import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux/es/exports";
import {useNavigate, useParams} from "react-router-dom";
import {getByExchangeId, saveExchange} from "../../services/exchangeService";
import ComboboxComponent from "../../components/Combobox";
import {RiExchangeFundsLine} from "@react-icons/all-files/ri/RiExchangeFundsLine";
import InputDate from "../../components/InputDate";
import Button from "../../components/Button";
import {MdOutlineDataSaverOff} from "react-icons/md";
import {getFetchData} from "../../services/supplierService";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

const AddExchangePage = () => {

    const {id} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {token} = useSelector((state) => state.user);
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fromCurrency, setFromCurrency] = useState([]);
    const [toCurrency, setToCurrency] = useState([]);
    const [form, setForm] = useState({
        inputFromCurrencyId: "",
        inputToCurrencyId: "",
        inputRate: null,
        inputDate: "",
    });

    const handleSubmit = async (evt) => {
        setIsLoading(true);
        evt.preventDefault();

        const response = await saveExchange(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/exchange`,
            token,
            {
                id: id ? id : null,
                date: evt.target.date.value,
                rate: evt.target.rate.value,
                fromCurrencyId: form.inputFromCurrencyId,
                toCurrencyId: form.inputToCurrencyId,
            }
        );
        if (response.success) {
            setIsLoading(false);
            navigate("/exchange");
            toast.success(id ? t('action.edit_success') : t('action.add_success'), {
                position: "bottom-right",
                autoClose: 3000,
                closeOnClick: true,
                className: "add-success"
            });
        } else {
            setIsLoading(false);
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    const fetchDataById = async () => {
        const response = await getByExchangeId(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/exchange/${id}`,
            token
        );
        if (response.success) {
            setForm({
                inputFromCurrencyId: response.data.fromCurrencyId,
                inputToCurrencyId: response.data.toCurrencyId,
                inputRate: response.data.rate,
                inputDate: response.data.date ? new Date(response.data.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            });
        } else {
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    const fetchDataCurrencyList = async (isAdd = false) => {
        const response = await getFetchData(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/currency/list`,
            token
        );
        if (response.success) {
            const mapped = response.data.map((item) => ({
                id: item.id,
                name: item.name + "  (" + item.symbol + ")",
            }));
            setFromCurrency(mapped);
            setToCurrency(mapped);

            if (isAdd) {
                const mainCurrency = response.data.find((item) => item.isMain);
                if (mainCurrency) {
                    setForm((prev) => ({...prev, inputFromCurrencyId: mainCurrency.id}));
                    setToCurrency(mapped.filter((item) => item.id !== mainCurrency.id));
                }
            }
        } else {
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    const onChangeFromCurrency = (value) => {
        setForm({...form, inputFromCurrencyId: value.id});
        setToCurrency(fromCurrency.filter((row) => row.id !== value.id));
    };

    const onChangeToCurrency = (value) => {
        setForm({...form, inputToCurrencyId: value.id});
    };

    useEffect(() => {
        if (id) {
            fetchDataCurrencyList();
            fetchDataById();
        } else {
            fetchDataCurrencyList(true);
            setForm((prev) => ({...prev, inputDate: new Date().toISOString().split('T')[0]}));
        }
    }, []);

    return (
        <>
            <div className="px-4 py-4 sm:px-6 lg:px-8">
                <h1 className="mb-5 text-xl font-semibold text-gray-900">
                    {id ? `${t(`table.exchange.edit`)}` : `${t(`table.exchange.add`)}`}
                </h1>
                <form onSubmit={handleSubmit}>
                    <div className="overflow-hidden shadow sm:rounded-md">
                        <div className="px-4 py-5 bg-white sm:p-6">
                            <div className="grid grid-cols-6 gap-6">

                                <div className="col-span-6 sm:col-span-3 flex justify-between items-end">
                                    <div className="w-full">
                                        <ComboboxComponent
                                            data={fromCurrency}
                                            item={
                                                form.inputFromCurrencyId
                                                    ? fromCurrency.find((item) => item.id === form.inputFromCurrencyId)
                                                    : null
                                            }
                                            label={t(`table.exchange.from_currency`)}
                                            onChange={onChangeFromCurrency}
                                        />
                                    </div>
                                    <div className="mx-5">
                                        <button type="button">
                                            <RiExchangeFundsLine className="h-8 w-8 text-blue-700"/>
                                        </button>
                                    </div>
                                    <div className="w-full">
                                        <ComboboxComponent
                                            data={toCurrency}
                                            item={
                                                form.inputToCurrencyId
                                                    ? toCurrency.find((item) => item.id === form.inputToCurrencyId)
                                                    : null
                                            }
                                            label={t(`table.exchange.to_currency`)}
                                            onChange={onChangeToCurrency}
                                        />
                                    </div>
                                </div>
                                <br/>
                                <div className="col-span-6 sm:col-span-3 flex justify-between items-center">
                                    <div className="w-full mr-5">
                                        <label
                                            htmlFor="rate"
                                            className="block text-sm font-medium text-gray-700 mb-3"
                                        >
                                            {t(`table.exchange.rate`)}
                                        </label>
                                        <input
                                            type="number"
                                            name="rate"
                                            min={0}
                                            id="rate"
                                            required
                                            autoComplete="rate"
                                            className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            defaultValue={form.inputRate}
                                        />
                                    </div>
                                    <div className="mx-6">
                                    </div>
                                    <div className="w-full">
                                        <InputDate defaultDate={form.inputDate}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end px-4 py-3 text-right bg-gray-50 sm:px-6">
                            <Button type="button" to="/exchange" btnName={t(`backward`)}/>
                            <button
                                type="submit"
                                className="text-white  bg-blue-700 hover:bg-blue-800  font-medium rounded-lg text-sm px-5 py-1.5 text-center  dark:bg-blue-600 dark:hover:bg-blue-700  inline-flex items-center"
                            >
                                {t(`save`)}
                                {isLoading ? (
                                    <svg
                                        role="status"
                                        className="inline w-4 h-4 ml-1 text-white animate-spin"
                                        viewBox="0 0 100 101"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                            fill="#E5E7EB"
                                        />
                                        <path
                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                ) : (
                                    <MdOutlineDataSaverOff className="w-4 h-4 ml-1"/>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};
export default AddExchangePage;
